import { getBooleanField, getTextField } from '@lender/utils';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import _ from 'lodash';
import { array, object, ValidationError } from 'yup';

import { FeeCicReasonType, SellerAdjustmentType } from 'constants/loanApplication';
import { LoanFeePayerEnum } from 'constants/loanFee';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { getPatch } from 'utils/patchValue';

import { borrowerPaidAmountFields, FEE_PATCH_KEYS } from './Section.constants';

export const getDefaultSchema = () => {
  return array().of(
    object()
      .shape({
        recordingType: getTextField({ isRequired: false }).nullable(),
        feeTypeName: getTextField({ isRequired: false }).nullable(),
        feeTypeId: getTextField({ isRequired: false }).nullable(),
        feeProviderId: getTextField({ isRequired: false }).nullable(),
        borrowerPaidAtClosingAmount: getTextField({ isRequired: false }).nullable(),
        borrowerPaidBeforeClosingAmount: getTextField({ isRequired: false }).nullable(),
        sellerPaidAtClosingAmount: getTextField({ isRequired: false }).nullable(),
        sellerPaidBeforeClosingAmount: getTextField({ isRequired: false }).nullable(),
        paidByOthersAmount: getTextField({ isRequired: false }).nullable(),
        financedAmount: getTextField({ isRequired: false }).nullable(),
      })
      .test(function () {
        const reqMsg = 'Required field should not be empty';

        const { pendingChangesActionType, feeTypeId, feeTypeName } = _.get(
          this.options.context.form,
          this.path,
        );

        const isDeleted = pendingChangesActionType === PendingChangesActionType.DELETED;

        const isSomeFalsy = [feeTypeId, feeTypeName].some(i => !i);

        if (isSomeFalsy && !isDeleted) {
          return this.createError({
            message: () =>
              [
                !feeTypeId && new ValidationError(reqMsg, null, `${this.path}.feeTypeId`),
                !feeTypeName && new ValidationError(reqMsg, null, `${this.path}.feeTypeName`),
              ].filter(Boolean),
          });
        }

        return true;
      })
      .test(function () {
        const reqMsg = 'Required field should not be empty';

        const fee = _.get(this.options.context.form, this.path);

        const isDeleted = fee.pendingChangesActionType === PendingChangesActionType.DELETED;

        const isAmountFieldEmpty = [
          fee.borrowerPaidAtClosingAmount,
          fee.borrowerPaidBeforeClosingAmount,
          fee.sellerPaidAtClosingAmount,
          fee.sellerPaidBeforeClosingAmount,
          fee.paidByOthersAmount,
        ].every(v => !v);

        if (isAmountFieldEmpty && !isDeleted) {
          return this.createError({
            message: () =>
              [
                new ValidationError(reqMsg, null, `${this.path}.borrowerPaidAtClosingAmount`),
                new ValidationError(reqMsg, null, `${this.path}.borrowerPaidBeforeClosingAmount`),
                new ValidationError(reqMsg, null, `${this.path}.sellerPaidAtClosingAmount`),
                new ValidationError(reqMsg, null, `${this.path}.sellerPaidBeforeClosingAmount`),
                new ValidationError(reqMsg, null, `${this.path}.paidByOthersAmount`),
              ].filter(Boolean),
          });
        }

        return true;
      }),
  );
};

export const getSchemaPrepaids = () => {
  return array().of(
    object().shape({
      feeTypeName: getTextField().nullable(),
      feeTypeId: getTextField().nullable(),
      feeProviderId: getTextField({ isRequired: false })
        .nullable()
        .when('isDeleted', {
          is: true,
          then: schema => schema.notRequired(),
          otherwise: schema => schema,
        })
        .when('pendingChangesActionType', {
          is: PendingChangesActionType.DELETED,
          then: schema => schema.notRequired(),
          otherwise: schema => schema,
        }),
      annualAmount: getTextField(),
      months: getTextField(),
      totalCollected: getTextField({ isRequired: false }),
      isDeleted: getBooleanField({ isRequired: false }).nullable(),
      pendingChangesActionType: getTextField({ isRequired: false }).nullable(),
    }),
  );
};

export const getSchemaInterestCredit = () =>
  object().shape({
    hasInterestCredit: getTextField(),
  });

// Typed amounts are strings; Number() stops _.sumBy concatenating them ('75' + 0 = '750').
const getBorrowerPaidTotal = fee =>
  _.sumBy(borrowerPaidAmountFields, prop => Number(fee?.[prop]) || 0);

const getIsBaselineBPAmountGT = i =>
  _.gt(getBorrowerPaidTotal(i), getBorrowerPaidTotal(i.cureBaseline));

// A reason already on the fee (e.g. the automatic Material Loan Term Change) stays selectable
// alongside the fee type's linked reasons so it can be replaced.
const getAvailableCicFeeReasons = (fee, { cicFeeReasons, feesWithReason }) => {
  if (!getIsBaselineBPAmountGT(fee)) {
    return null;
  }

  const feeTypeId = fee.feeTypeId?.value;
  const currentReasonIds = _.keys(feesWithReason?.[feeTypeId]?.notes);

  const linkedReasons = cicFeeReasons.filter(reason =>
    _.some(reason.items, reasonItem => reasonItem.feeTypeId === feeTypeId),
  );
  const reasons = _.uniqBy(
    [...linkedReasons, ...cicFeeReasons.filter(reason => currentReasonIds.includes(reason.value))],
    'value',
  );

  return _.isEmpty(reasons) ? null : reasons;
};

// Only the fee fields the user touched on this row and that differ from the loaded fee become Direct patch values, so
// the backend credits a single named change instead of every payer amount the whole-fee payload re-sends.
const getFeePatch = ({ fee, feeField, rowPath, userEditedFieldNames }) => {
  if (!userEditedFieldNames || !rowPath) {
    return null;
  }

  const editedKeys = FEE_PATCH_KEYS.filter(key => userEditedFieldNames.has(`${rowPath}.${key}`));

  if (_.isEmpty(editedKeys)) {
    return null;
  }

  const patch = getPatch({
    current: fee,
    baseline: {
      ...feeField.initialFee,
      feeProviderId: feeField.initialFee?.feeProviderId?.value,
    },
    keys: editedKeys,
  });

  return _.isEmpty(patch) ? null : patch;
};

export const getEditFeeActionData = ({
  data,
  userEditedFieldNames,
  rowPaths = [],
  cicFeeReasons,
  loanId,
  isSkip,
  feesWithReason,
  pendingCredits = [],
  cureBaseline,
}) => {
  const groupedData = _.groupBy(data, 'feeTypeId.value');

  const rowPathByField = new Map(data.map((field, index) => [field, rowPaths[index]]));

  const filteredFormData = data.reduce((formData, field) => {
    const isRestoredFee =
      _.size(groupedData[field.feeTypeId.value]) > 1 &&
      field.pendingChangesActionType === PendingChangesActionType.DELETED;

    if (isRestoredFee) {
      return formData;
    }

    return field.isDeleted &&
      data.find(formField => field.feeTypeId === formField.feeTypeId && !formField.isDeleted)
      ? formData
      : [...formData, field];
  }, []);

  const extendedForm = filteredFormData.map(feeField => {
    // Only fees loaded from the server carry their baseline; a fee added in this edit takes its fee type's entry.
    const feeWithBaseline = feeField.cureBaseline
      ? feeField
      : { ...feeField, cureBaseline: cureBaseline?.[feeField.feeTypeId?.value] };
    const borrowerFeePayer =
      !!(feeField.borrowerPaidAtClosingAmount || feeField.borrowerPaidBeforeClosingAmount) &&
      LoanFeePayerEnum.BORROWER;
    const sellerFeePayer =
      !!(feeField.sellerPaidAtClosingAmount || feeField.sellerPaidBeforeClosingAmount) &&
      LoanFeePayerEnum.SELLER;
    const otherFeePayer = !!feeField.paidByOthersAmount && LoanFeePayerEnum.OTHER;

    const isDeleted = feeField.pendingChangesActionType === PendingChangesActionType.DELETED;

    if (isDeleted) {
      return {
        ...feeField.initialFee,
        isDeleted,
        feeTypeId: feeField.initialFee.feeTypeId?.value,
        feeProviderId: feeField.initialFee.feeProviderId?.value,
      };
    }

    const fee = {
      ..._.omit(feeField, 'appraisalSurcharges'),
      isDeleted,
      feeTypeName: feeField.isLocallyAdded ? feeField.feeTypeId?.name : feeField.feeTypeName,
      feeTypeId: feeField.feeTypeId?.value || feeField.feeTypeId,
      feeProviderName: feeField.feeProviderId?.name,
      feeProviderId: feeField.feeProviderId?.value,
      recordingType: feeField.recordingType?.value,
      feePayer: borrowerFeePayer || sellerFeePayer || otherFeePayer,
      beforeClosing: !!(
        feeField.borrowerPaidBeforeClosingAmount || feeField.sellerPaidBeforeClosingAmount
      ),
      borrowerPaidBeforeClosingAmount: Number(feeField.borrowerPaidBeforeClosingAmount) || 0,
      sellerPaidBeforeClosingAmount: Number(feeField.sellerPaidBeforeClosingAmount) || 0,
      borrowerPaidAtClosingAmount: Number(feeField.borrowerPaidAtClosingAmount) || 0,
      sellerPaidAtClosingAmount: Number(feeField.sellerPaidAtClosingAmount) || 0,
      paidByOthersAmount: Number(feeField.paidByOthersAmount) || 0,
      financedAmount: Number(feeField.financedAmount) || 0,
      availableReasons: getAvailableCicFeeReasons(feeWithBaseline, {
        cicFeeReasons,
        feesWithReason,
      }),
      isBaselineBPAmountGT: getIsBaselineBPAmountGT(feeWithBaseline),
      isIterationBPAmountEQ: _.eq(
        getBorrowerPaidTotal(feeField),
        getBorrowerPaidTotal(feeField.initialFee),
      ),
    };

    const patch = getFeePatch({
      fee,
      feeField,
      rowPath: rowPathByField.get(feeField),
      userEditedFieldNames,
    });

    return patch ? { ...fee, patch } : fee;
  });

  return {
    changes: extendedForm.map(fee => {
      const reasons = _.map(feesWithReason[fee.feeTypeId]?.notes, (note, cicFeeReasonId) => ({
        cicFeeReasonId,
        note,
      }));

      const credits = pendingCredits.reduce((acc, credit) => {
        if (credit.feeTypeId === fee.feeTypeId) {
          acc.push({ amount: credit.amount, creditSource: credit.creditSource });
        }

        return acc;
      }, []);

      return {
        fee,
        reasons,
        credits: credits.length ? credits : undefined,
        type:
          !_.isEmpty(reasons) && fee.isBaselineBPAmountGT && !isSkip
            ? FeeCicReasonType.WITH_CIC_REASON
            : FeeCicReasonType.WITHOUT_REASON,
      };
    }),
    loanId,
    requestMetadata: { tabId: LoanApplicationTabId.LOAN_FEES },
  };
};

// A fee removed in the form (isDeleted) is sent as a delete item. A change typed WithoutReason (skipped or not required) must not carry reasons: the backend no longer reads the type.
export const getFeesPatchRequest = ({ changes, loanId, requestMetadata }) => ({
  loanId,
  fees: changes.reduce((fees, { fee, reasons, credits, type }) => {
    if (fee.isDeleted) {
      // A fee the server already has pending removal has nothing left to delete: a recalculation drops its row, and
      // a delete for a missing row fails the whole save. Only a fee removed in this form carries another action type.
      return fee.pendingChangesActionType === PendingChangesActionType.DELETED
        ? fees
        : [...fees, { feeTypeId: fee.feeTypeId, patch: {}, delete: true }];
    }

    const item = {
      feeTypeId: fee.feeTypeId,
      patch: fee.patch ?? {},
      reasons: type === FeeCicReasonType.WITH_CIC_REASON ? reasons : [],
      credits,
    };

    return _.isEmpty(item.patch) && _.isEmpty(item.reasons) && _.isEmpty(item.credits)
      ? fees
      : [...fees, item];
  }, []),
  requestMetadata,
});

export const getSchemaAdjustments = () => {
  return array().of(
    object().shape({
      sellerAdjustmentTypeId: getTextField().nullable(),
      startDate: getTextField().nullable(),
      endDate: getTextField().nullable(),
      totalAmount: getTextField().nullable(),
      isDeleted: getBooleanField({ isRequired: false }).nullable(),
      pendingChangesActionType: getTextField({ isRequired: false }).nullable(),
    }),
  );
};

const getFormAdjustmentForSend = formAdjustment => ({
  applicationSellerAdjustmentId: formAdjustment.applicationSellerAdjustmentId,
  type: formAdjustment.type,
  sellerAdjustmentTypeId: formAdjustment.sellerAdjustmentTypeId.value,
  totalAmount: Number(formAdjustment.totalAmount),
  startDate: formAdjustment.startDate,
  endDate: formAdjustment.endDate,
});

const getAdjustmentForSend = ({
  applicationSellerAdjustmentId,
  type,
  sellerAdjustmentTypeId,
  totalAmount,
  startDate,
  endDate,
}) => ({
  applicationSellerAdjustmentId,
  type,
  sellerAdjustmentTypeId,
  totalAmount,
  startDate,
  endDate,
});

export const getFormAdjustmentsForUpdate = ({
  formAdjustments,
  adjustmentType = SellerAdjustmentType.PAID,
}) =>
  formAdjustments.reduce((acc, adjustment) => {
    if (adjustment.pendingChangesActionType === PendingChangesActionType.DELETED) {
      return acc;
    }

    acc.push(getFormAdjustmentForSend({ ...adjustment, type: adjustmentType }));

    return acc;
  }, []);

export const getAdjustmentsForUpdate = adjustmentsFormSet =>
  adjustmentsFormSet.reduce((acc, adjustment) => {
    if (adjustment.pendingChangesActionType === PendingChangesActionType.DELETED) {
      return acc;
    }

    acc.push(getAdjustmentForSend(adjustment));

    return acc;
  }, []);

// Merge re-mapped server rows with every unsaved locally-added row, so a background data refresh
// that re-fires the rebuild effect keeps all rows the user is still adding, not just the last one.
export const getRowsWithLocallyAdded = ({ initialRows, currentRows }) => [
  ...initialRows,
  ...currentRows.filter(row => row.isLocallyAdded),
];

export const getInitialAdjustmentsFormValues = ({ adjustments, adjustmentTypeOptions }) =>
  adjustments.map(adjustment => {
    const { pendingChangesActionType = PendingChangesActionType.NO_CHANGES, modifiedFields = [] } =
      adjustment || {};

    const formattedFee = {
      ...adjustment,
      totalAmount: String(adjustment.totalAmount),
      sellerAdjustmentTypeId: adjustmentTypeOptions.find(
        i => i.value === adjustment.sellerAdjustmentTypeId,
      ),
      pendingChangesActionType,
      modifiedFields,
    };

    return {
      ...formattedFee,
      initialFee: formattedFee,
    };
  });
